import { useTranslation } from "react-i18next";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@nexus/ui";
import {
  EVENT_TIME_RANGES,
  eventTimeRangeLabel,
} from "@/features/events/constants/event-time-ranges";
import {
  NumberField,
  SettingRow,
  SettingSwitch,
  SettingsCard,
} from "@/features/changelog/components/shared/SettingsCard";
import type { AdminOpsSettingsDraft } from "@/features/admin/lib/admin-ops-settings";
import {
  AUTO_REFRESH_DEFAULT_SECONDS,
  GROUPINGS,
  LIMITS,
  RETENTION_DEFAULT_DAYS,
} from "./ops-settings-limits";
import type { OpsSettingsCardProps } from "./types";

/**
 * What the token-usage tab keeps and what it opens showing. The thresholds that
 * post to Slack are a separate group: reading five of them interleaved with
 * display defaults made both halves harder to scan.
 */
export function TokenUsageSettingsCard({ draft, set, isReadOnly }: OpsSettingsCardProps) {
  const { t } = useTranslation();
  const retentionOn = draft.tokenUsageRetentionDays > 0;
  const autoRefreshOn = draft.tokenUsageAutoRefreshSeconds > 0;

  return (
    <SettingsCard
      icon="sparkles"
      title={t("admin.opsSettings.tokenUsage.title", "Token usage")}
      description={t(
        "admin.opsSettings.tokenUsage.description",
        "What every LLM call cost in tokens. How long that record is kept, and what the tab opens showing.",
      )}
    >
      <SettingRow
        htmlFor="token-usage-retention"
        label={t("admin.opsSettings.tokenRetention", "Delete old token usage")}
        description={t(
          "admin.opsSettings.tokenRetentionHelp",
          "Off keeps every call forever. On, calls older than the window below are deleted for good — this table is the only per-call record of what was billed.",
        )}
        control={
          <SettingSwitch
            id="token-usage-retention"
            checked={retentionOn}
            disabled={isReadOnly}
            onCheckedChange={(checked) =>
              set("tokenUsageRetentionDays", checked ? RETENTION_DEFAULT_DAYS : 0)
            }
          />
        }
      />

      <SettingRow
        htmlFor="token-usage-retention-days"
        isNested
        isMuted={!retentionOn}
        label={t("admin.opsSettings.tokenRetentionDays", "Keep token usage for")}
        description={t(
          "admin.opsSettings.tokenRetentionDaysHelp",
          "Old calls are cleaned up about once an hour, whenever somebody has an admin tab open.",
        )}
        control={
          <NumberField
            id="token-usage-retention-days"
            value={retentionOn ? draft.tokenUsageRetentionDays : RETENTION_DEFAULT_DAYS}
            min={LIMITS.tokenUsageRetentionDays.min}
            max={LIMITS.tokenUsageRetentionDays.max}
            unit={t("admin.opsSettings.days", "days")}
            disabled={isReadOnly || !retentionOn}
            onChange={(value) => set("tokenUsageRetentionDays", value)}
          />
        }
      />

      <SettingRow
        label={t("admin.opsSettings.tokenDefaultWindow", "Open showing")}
        description={t(
          "admin.opsSettings.tokenDefaultWindowHelp",
          "The stretch of usage the tab starts on. The range picker on the tab still changes it per visit.",
        )}
        control={
          <Select
            value={String(draft.tokenUsageDefaultWindowHours)}
            disabled={isReadOnly}
            onValueChange={(value) => set("tokenUsageDefaultWindowHours", Number(value))}
          >
            <SelectTrigger className="h-8 w-44 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EVENT_TIME_RANGES.map((range) => (
                <SelectItem key={range.value} value={String(range.hours)} className="text-xs">
                  {eventTimeRangeLabel(range.value, t)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      <SettingRow
        htmlFor="token-usage-auto-refresh"
        label={t("admin.opsSettings.tokenAutoRefresh", "Refresh the numbers automatically")}
        description={t(
          "admin.opsSettings.tokenAutoRefreshHelp",
          "Useful while watching a generation run. The Vertex panel keeps its own slower cadence either way, because Cloud Monitoring bills per read.",
        )}
        control={
          <SettingSwitch
            id="token-usage-auto-refresh"
            checked={autoRefreshOn}
            disabled={isReadOnly}
            onCheckedChange={(checked) =>
              set("tokenUsageAutoRefreshSeconds", checked ? AUTO_REFRESH_DEFAULT_SECONDS : 0)
            }
          />
        }
      />

      <SettingRow
        htmlFor="token-usage-auto-refresh-seconds"
        isNested
        isMuted={!autoRefreshOn}
        label={t("admin.opsSettings.tokenAutoRefreshEvery", "Refresh every")}
        description={t(
          "admin.opsSettings.tokenAutoRefreshEveryHelp",
          "Usage is written as each call finishes, so a minute keeps the strip close to live.",
        )}
        control={
          <NumberField
            id="token-usage-auto-refresh-seconds"
            value={autoRefreshOn ? draft.tokenUsageAutoRefreshSeconds : AUTO_REFRESH_DEFAULT_SECONDS}
            min={LIMITS.tokenUsageAutoRefreshSeconds.min}
            max={LIMITS.tokenUsageAutoRefreshSeconds.max}
            unit={t("admin.opsSettings.seconds", "seconds")}
            disabled={isReadOnly || !autoRefreshOn}
            onChange={(value) => set("tokenUsageAutoRefreshSeconds", value)}
          />
        }
      />

      <SettingRow
        label={t("admin.opsSettings.tokenDefaultGrouping", "Open the chart grouped by")}
        description={t(
          "admin.opsSettings.tokenDefaultGroupingHelp",
          "What the bars are split by before anyone touches the control. Tenant answers who is spending; feature answers on what.",
        )}
        control={
          <Select
            value={draft.tokenUsageDefaultGrouping}
            disabled={isReadOnly}
            onValueChange={(value) =>
              set(
                "tokenUsageDefaultGrouping",
                value as AdminOpsSettingsDraft["tokenUsageDefaultGrouping"],
              )
            }
          >
            <SelectTrigger className="h-8 w-44 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {GROUPINGS.map((grouping) => (
                <SelectItem key={grouping} value={grouping} className="text-xs">
                  {t(`admin.opsSettings.grouping.${grouping}`, grouping)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      <SettingRow
        htmlFor="token-usage-long-tail-percent"
        label={t("admin.opsSettings.tokenLongTail", "Count a row as long tail under")}
        description={t(
          "admin.opsSettings.tokenLongTailHelp",
          "What the \u201cHide under\u201d chip on each usage table folds away. The chip still has to be switched on — this only sets where the line falls.",
        )}
        control={
          <NumberField
            id="token-usage-long-tail-percent"
            value={draft.tokenUsageLongTailPercent}
            min={LIMITS.tokenUsageLongTailPercent.min}
            max={LIMITS.tokenUsageLongTailPercent.max}
            unit={t("admin.opsSettings.percentOfWindow", "% of the window")}
            disabled={isReadOnly}
            onChange={(value) => set("tokenUsageLongTailPercent", value)}
          />
        }
      />
    </SettingsCard>
  );
}
